import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { money } from "@/data/site";
import { PoundSterling, Boxes, ShoppingCart, AlertTriangle, Users, Package, PackageX } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Overview() {
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);
  const [range, setRange] = useState(14);
  useEffect(() => { staffApi.get("/admin/overview").then((r) => setData(r.data)); }, []);
  useEffect(() => {
    setSales(null);
    staffApi.get("/admin/sales-snapshot", { params: { days: range } }).then((r) => setSales(r.data));
  }, [range]);
  if (!data) return <p className="text-cream/50">Loading metrics…</p>;

  const cards = [
    { icon: PoundSterling, label: "Inventory Value", value: money(data.inventory_value) },
    { icon: Boxes, label: "Units in Stock", value: data.total_units },
    { icon: Package, label: "Active Products", value: data.product_count },
    { icon: ShoppingCart, label: "Orders", value: data.order_count },
    { icon: PoundSterling, label: "Revenue", value: money(data.revenue) },
    { icon: Users, label: "Members", value: data.member_count },
  ];

  return (
    <div data-testid="overview-page">
      <PageHead kicker="Command Center" title="Overview" />
      {(data.low_stock_count > 0 || data.out_of_stock_count > 0) && (
        <div className="mb-6 flex flex-wrap items-center gap-3 border border-yellow-600/40 bg-yellow-600/10 px-5 py-4 text-sm text-yellow-300" data-testid="low-stock-alert">
          <AlertTriangle size={18} />
          <span>
            {data.out_of_stock_count > 0 && <><strong className="text-red-400">{data.out_of_stock_count}</strong> variant{data.out_of_stock_count !== 1 ? "s" : ""} out of stock · </>}
            <strong>{data.low_stock_count}</strong> variant{data.low_stock_count !== 1 ? "s" : ""} running low. Review the stock alerts below.
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="border border-white/10 bg-ink p-5" data-testid={`metric-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <c.icon size={18} className="mb-3 text-gold" />
            <p className="overline-label text-[0.6rem]">{c.label}</p>
            <p className="mt-1 font-mono text-2xl text-cream">{c.value}</p>
          </div>
        ))}
      </div>

      {sales && (
        <div className="mt-8 border border-white/10 bg-ink p-5" data-testid="sales-snapshot">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-serif text-xl text-cream">Sales Snapshot</h2>
              <p className="text-xs text-cream/40">Last {sales.days} days</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex border border-white/15" data-testid="sales-range-toggle">
                {[7, 14, 30].map((d) => (
                  <button key={d} onClick={() => setRange(d)} data-testid={`sales-range-${d}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest transition ${range === d ? "bg-gold text-black" : "text-cream/60 hover:text-gold"}`}>{d}d</button>
                ))}
              </div>
              <div className="flex gap-6 text-right">
                <div><p className="overline-label text-[0.6rem]">Revenue</p><p className="font-mono text-lg text-gold">{money(sales.total_revenue)}</p></div>
                <div><p className="overline-label text-[0.6rem]">Units</p><p className="font-mono text-lg text-cream">{sales.total_units}</p></div>
                <div><p className="overline-label text-[0.6rem]">Orders</p><p className="font-mono text-lg text-cream">{sales.total_orders}</p></div>
              </div>
            </div>
          </div>
          <div className="h-56 w-full" data-testid="sales-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={224}>
              <AreaChart data={sales.series} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#cda95c" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#cda95c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fill: "rgba(245,240,230,0.4)", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <YAxis tick={{ fill: "rgba(245,240,230,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => `C$${v}`} />
                <Tooltip
                  contentStyle={{ background: "#0b0b0b", border: "1px solid rgba(205,169,92,0.4)", fontSize: 12 }}
                  labelStyle={{ color: "#cda95c" }} itemStyle={{ color: "#f5f0e6" }}
                  formatter={(value, name) => name === "revenue" ? [money(value), "Revenue"] : [value, "Units"]} />
                <Area type="monotone" dataKey="revenue" stroke="#cda95c" strokeWidth={2} fill="url(#revFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
        <div className="mt-10" data-testid="stock-status-panel">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-serif text-xl text-cream"><AlertTriangle size={18} className="text-yellow-400" /> Stock Alerts</h2>
            <Link to="/staff/dashboard/inventory" data-testid="stock-manage-link" className="text-xs uppercase tracking-widest text-gold hover:text-gold-bright">Manage inventory →</Link>
          </div>
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full min-w-[560px] text-sm" data-testid="stock-alerts-table">
              <thead><tr className="bg-ink text-gold/80">{["Product", "Variant", "SKU", "Stock", "Status"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
              <tbody>
                {data.low_stock_items.map((it) => (
                  <tr key={it.variant_id} className={`border-t border-white/10 ${it.out_of_stock ? "bg-red-500/5" : ""}`} data-testid={`stock-alert-${it.variant_id}`}>
                    <td className="px-4 py-3 text-cream"><Link to={`/product/${it.slug}`} className="hover:text-gold">{it.product_name}</Link></td>
                    <td className="px-4 py-3 text-cream/70">{it.color} · {it.size}</td>
                    <td className="px-4 py-3 font-mono text-xs text-cream/50">{it.sku}</td>
                    <td className={`px-4 py-3 font-mono ${it.out_of_stock ? "text-red-400" : "text-yellow-400"}`}>{it.quantity}</td>
                    <td className="px-4 py-3">
                      {it.out_of_stock
                        ? <span className="inline-flex items-center gap-1 border border-red-500/40 px-2 py-1 text-[0.6rem] uppercase tracking-widest text-red-400"><PackageX size={11} /> Out of stock</span>
                        : <span className="inline-flex items-center gap-1 border border-yellow-500/40 px-2 py-1 text-[0.6rem] uppercase tracking-widest text-yellow-400"><AlertTriangle size={11} /> Low</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2 className="mb-4 mt-10 font-serif text-xl text-cream">Recent Orders</h2>
      <div className="border border-white/10">
        {data.recent_orders.length === 0 ? <p className="p-8 text-center text-sm text-cream/50">No orders yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="bg-ink text-gold/80">{["Order", "Email", "Total", "Status"].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
            <tbody>{data.recent_orders.map((o) => (
              <tr key={o.id} className="border-t border-white/10 text-cream/80">
                <td className="px-4 py-3 font-mono text-gold">{o.order_number}</td>
                <td className="px-4 py-3">{o.email}</td>
                <td className="px-4 py-3 font-mono">{money(o.total)}</td>
                <td className="px-4 py-3 capitalize">{o.status}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
